import React, { useState, useEffect, useCallback } from 'react';
import { QrCode, Shield, RefreshCw, Maximize2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';
import AtelierLayout from '@/components/atelier/AtelierLayout';
import { QRCodeSVG } from 'qrcode.react';

const AtelierPointage = ({ onLogout }) => {
  const [qrToken, setQrToken] = useState('');
  const [countdown, setCountdown] = useState(30);
  const [blocked, setBlocked] = useState(false);

  const fetchToken = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/timeclock/qr-token`);
      setQrToken(res.data.token);
      setCountdown(res.data.ttl || 30);
      setBlocked(false);
    } catch (err) {
      if (err.response?.status === 403) setBlocked(true);
    }
  }, []);

  useEffect(() => {
    fetchToken();
    const interval = setInterval(fetchToken, 30000);
    return () => clearInterval(interval);
  }, [fetchToken]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { fetchToken(); return 30; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchToken]);

  return (
    <AtelierLayout onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-lg mx-auto space-y-4" data-testid="atelier-pointage-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <QrCode className="w-6 h-6 text-blue-600" /> Pointage
          </h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('/timeclock-qr', '_blank')}
            data-testid="fullscreen-qr-btn"
          >
            <Maximize2 className="w-4 h-4 mr-1" /> Plein ecran
          </Button>
        </div>

        <Card className="p-6 rounded-xl" data-testid="atelier-qr-card">
          {blocked ? (
            <div className="text-center py-10 space-y-3" data-testid="qr-blocked-msg">
              <Shield className="w-12 h-12 text-red-400 mx-auto" />
              <p className="text-base font-bold text-red-600">Acces reseau non autorise</p>
              <p className="text-sm text-slate-500">
                Ce poste n'est pas autorise a afficher le QR de pointage.
              </p>
              <p className="text-xs text-slate-400">
                Demandez a l'administrateur d'ajouter l'adresse IP de ce poste dans
                Parametres &gt; Timbrage &gt; IPs autorisees
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-5">
              <p className="text-sm text-slate-600 text-center">
                Les employes scannent ce code avec leur telephone pour pointer
              </p>
              <div className="bg-white rounded-2xl p-4 shadow-md border border-slate-100" data-testid="qr-code-display">
                {qrToken ? (
                  <QRCodeSVG
                    value={`VORTEKIO-PUNCH:${qrToken}`}
                    size={260}
                    level="M"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                  />
                ) : (
                  <div className="w-[260px] h-[260px] flex items-center justify-center">
                    <RefreshCw className="w-10 h-10 text-slate-300 animate-spin" />
                  </div>
                )}
              </div>
              {/* Countdown */}
              <div className="w-full max-w-xs flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-1000"
                    style={{ width: `${(countdown / 30) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-mono text-slate-400 w-8">{countdown}s</span>
              </div>
              <div className="flex items-center gap-1.5 text-emerald-600">
                <Shield className="w-4 h-4" />
                <span className="text-xs font-medium">Code rotatif securise — renouvele toutes les 30s</span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AtelierLayout>
  );
};

export default AtelierPointage;
